import { fg } from '@atlassian/jira-feature-gating';
import {
	ASSIGNEE_TYPE,
	ATTACHMENT_TYPE,
	CASCADING_SELECT_CF_TYPE,
	CATEGORY_TYPE,
	COMPONENTS_TYPE,
	DATETIME_CF_TYPE,
	DATE_CF_TYPE,
	DESCRIPTION_TYPE,
	DUE_DATE_TYPE,
	GROUP_CF_TYPE,
	LABELS_CF_TYPE,
	LABELS_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	MULTI_GROUP_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	MULTI_USER_CF_TYPE,
	NUMBER_CF_TYPE,
	PARENT_TYPE,
	PEOPLE_CF_TYPE,
	PRIORITY_TYPE,
	RADIO_BUTTONS_CF_TYPE,
	SELECT_CF_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	SUMMARY_TYPE,
	TEAMS_PLATFORM_CF_TYPE,
	TEAM_CF_TYPE,
	TEXT_AREA_CF_TYPE,
	TEXT_CF_TYPE,
	URL_CF_TYPE,
	USER_CF_TYPE,
} from '@atlassian/jira-platform-field-config';
import { AssigneeFieldTypeConfig } from './field-types/assignee-field-type';
import { AttachmentFieldTypeConfig } from './field-types/attachment-field-type';
import { CascadingSelectFieldTypeConfig } from './field-types/cascading-select-custom-field-type';
import { CategoryFieldTypeConfig } from './field-types/category-field-type';
import { ComponentsFieldTypeConfig } from './field-types/components-field-type';
import { DateCustomFieldTypeConfig } from './field-types/date-custom-field-type';
import { DateTimeCustomFieldTypeConfig } from './field-types/date-time-custom-field-type';
import { DescriptionFieldTypeConfig } from './field-types/description-field-type';
import { DueDateFieldTypeConfig } from './field-types/due-date-field-type';
import { GroupCustomFieldTypeConfig } from './field-types/group-custom-field-type';
import { LabelsCustomFieldTypeConfig } from './field-types/labels-custom-field-type';
import { LabelsFieldTypeConfig } from './field-types/labels-field-type';
import { MultiCheckboxesCustomFieldTypeConfig } from './field-types/multi-checkboxes-custom-field-type';
import { MultiGroupCustomFieldTypeConfig } from './field-types/multi-group-custom-field-type';
import { MultiSelectCustomFieldTypeConfig } from './field-types/multi-select-custom-field-type';
import { MultiUserCustomFieldTypeConfig } from './field-types/multi-user-custom-field-type';
import { NumberCustomFieldTypeConfig } from './field-types/number-custom-field-type';
import { ParentFieldTypeConfig } from './field-types/parent-field-type';
import { PeopleCustomFieldType } from './field-types/people-custom-type-field';
import { PriorityFieldTypeConfig } from './field-types/priority-field-type';
import { RadioButtonsCustomFieldTypeConfig } from './field-types/radio-buttons-custom-field-type';
import { SelectCustomFieldTypeConfig } from './field-types/select-custom-field-type';
import { StoryPointEstimateCustomFieldTypeConfig } from './field-types/story-point-estimate-custom-field-type';
import { SummaryFieldTypeConfig } from './field-types/summary-field-type';
import { TeamCustomFieldTypeConfig } from './field-types/team-custom-field-type';
import { TeamsPlatformCustomFieldTypeConfig } from './field-types/teams-platform-custom-field-type';
import { TextAreaCustomFieldTypeConfig } from './field-types/text-area-custom-field-type';
import { TextCustomFieldTypeConfig } from './field-types/text-custom-field-type';
import { UrlCustomFieldTypeConfig } from './field-types/url-custom-field-type';
import { UserCustomFieldTypeConfig } from './field-types/user-custom-field-type';
import type { FieldTypeConfig, SupportedFieldType } from './types';

const EXTENDED_CONFIGS_TYPES = [
	PEOPLE_CF_TYPE,
	CATEGORY_TYPE,
	PARENT_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	COMPONENTS_TYPE,
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const FIELD_TYPE_CONFIGS: Record<SupportedFieldType, FieldTypeConfig<any, any>> = {
	[ASSIGNEE_TYPE]: AssigneeFieldTypeConfig,
	[ATTACHMENT_TYPE]: AttachmentFieldTypeConfig,
	[CASCADING_SELECT_CF_TYPE]: CascadingSelectFieldTypeConfig,
	[CATEGORY_TYPE]: CategoryFieldTypeConfig,
	[COMPONENTS_TYPE]: ComponentsFieldTypeConfig,
	[DATETIME_CF_TYPE]: DateTimeCustomFieldTypeConfig,
	[DATE_CF_TYPE]: DateCustomFieldTypeConfig,
	[DESCRIPTION_TYPE]: DescriptionFieldTypeConfig,
	[DUE_DATE_TYPE]: DueDateFieldTypeConfig,
	[GROUP_CF_TYPE]: GroupCustomFieldTypeConfig,
	[LABELS_CF_TYPE]: LabelsCustomFieldTypeConfig,
	[LABELS_TYPE]: LabelsFieldTypeConfig,
	[MULTI_CHECKBOXES_CF_TYPE]: MultiCheckboxesCustomFieldTypeConfig,
	[MULTI_GROUP_CF_TYPE]: MultiGroupCustomFieldTypeConfig,
	[MULTI_SELECT_CF_TYPE]: MultiSelectCustomFieldTypeConfig,
	[MULTI_USER_CF_TYPE]: MultiUserCustomFieldTypeConfig,
	[NUMBER_CF_TYPE]: NumberCustomFieldTypeConfig,
	[PARENT_TYPE]: ParentFieldTypeConfig,
	[PEOPLE_CF_TYPE]: PeopleCustomFieldType,
	[PRIORITY_TYPE]: PriorityFieldTypeConfig,
	[RADIO_BUTTONS_CF_TYPE]: RadioButtonsCustomFieldTypeConfig,
	[SELECT_CF_TYPE]: SelectCustomFieldTypeConfig,
	[STORY_POINT_ESTIMATE_CF_TYPE]: StoryPointEstimateCustomFieldTypeConfig,
	[SUMMARY_TYPE]: SummaryFieldTypeConfig,
	[TEAMS_PLATFORM_CF_TYPE]: TeamsPlatformCustomFieldTypeConfig,
	[TEAM_CF_TYPE]: TeamCustomFieldTypeConfig,
	[TEXT_AREA_CF_TYPE]: TextAreaCustomFieldTypeConfig,
	[TEXT_CF_TYPE]: TextCustomFieldTypeConfig,
	[URL_CF_TYPE]: UrlCustomFieldTypeConfig,
	[USER_CF_TYPE]: UserCustomFieldTypeConfig,
};

export function isFieldTypeSupported(type: string): type is SupportedFieldType {
	if (!fg('forms_work-intake_rollout-1')) {
		return type in FIELD_TYPE_CONFIGS && !(type in EXTENDED_CONFIGS_TYPES);
	}

	return type in FIELD_TYPE_CONFIGS;
}

export function getFieldTypeConfig(fieldType: string) {
	return isFieldTypeSupported(fieldType) ? FIELD_TYPE_CONFIGS[fieldType] : undefined;
}
